import { LRUCache } from 'lru-cache';
import { calculatePdfSize } from './pdf-decoder';

// Constants
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;
const UTF16_BYTES_PER_CHAR = 2;
const FALLBACK_SIZE = BYTES_PER_KB;
const MIN_SIZE = 1;

/**
 * Generic LRU Cache utility for common caching needs
 */
class LRUCacheUtils {
  /**
   * Calculate size of a string (UTF-16 encoding: 2 bytes per character)
   * @param {string} str - String to calculate size for
   * @returns {number} Size in bytes
   */
  static _calculateStringSize(str) {
    return Math.max(MIN_SIZE, str.length * UTF16_BYTES_PER_CHAR);
  }

  /**
   * Calculate size of an object by JSON stringification
   * @param {*} value - Object to calculate size for
   * @param {number} fallbackSize - Size to return if serialization fails
   * @returns {number} Size in bytes
   */
  static _calculateObjectSize(value, fallbackSize = FALLBACK_SIZE) {
    try {
      const jsonString = JSON.stringify(value);
      return Math.max(MIN_SIZE, LRUCacheUtils._calculateStringSize(jsonString));
    } catch (e) {
      // Circular references or non-serializable objects
      return fallbackSize;
    }
  }

  /**
   * Create a new LRU cache instance with common defaults
   * @param {Object} options - Cache configuration options
   * @param {number} options.maxSize - Maximum size in bytes (default: 10 MB)
   * @param {number} options.ttl - Time to live in milliseconds (default: 1 hour)
   * @param {boolean} options.updateAgeOnGet - Update age when accessed (default: true)
   * @param {Function} options.sizeCalculation - Function to calculate item size in bytes
   * @param {Function} options.dispose - Function called when item is evicted
   * @returns {LRUCache} Configured LRU cache instance
   */
  static createCache(options = {}) {
    const defaultOptions = {
      maxSize: 10 * BYTES_PER_MB,
      ttl: 1000 * 60 * 60, // 1 hour
      updateAgeOnGet: true,
      sizeCalculation: (value) => {
        // Fast path: most common types first
        if (typeof value === 'string') {
          return LRUCacheUtils._calculateStringSize(value);
        }
        if (Array.isArray(value)) {
          // Rough estimate: 100 bytes per array element
          return Math.max(MIN_SIZE, value.length * 100);
        }
        if (value && typeof value === 'object') {
          return LRUCacheUtils._calculateObjectSize(value, FALLBACK_SIZE);
        }
        return MIN_SIZE; // Primitives (number, boolean, null, undefined)
      },
    };

    return new LRUCache({ ...defaultOptions, ...options });
  }

  /**
   * Create a cache specifically for API responses
   * @param {Object} options - Cache configuration options
   * @returns {LRUCache} Configured LRU cache for API responses
   */
  static createApiCache(options = {}) {
    const apiOptions = {
      maxSize: 20 * BYTES_PER_MB,
      ttl: 1000 * 60 * 30, // 30 minutes
      updateAgeOnGet: true,
      sizeCalculation: (value) => {
        // API responses are typically arrays or objects
        if (Array.isArray(value)) {
          // More accurate estimate: 200 bytes per array element
          return Math.max(MIN_SIZE, value.length * 200);
        }
        if (value && typeof value === 'object') {
          return LRUCacheUtils._calculateObjectSize(value, FALLBACK_SIZE);
        }
        if (typeof value === 'string') {
          return LRUCacheUtils._calculateStringSize(value);
        }
        return 1000; // Fallback for other types
      },
      ...options,
    };

    return LRUCacheUtils.createCache(apiOptions);
  }

  /**
   * Create a cache specifically for autocomplete suggestions
   * @param {Object} options - Cache configuration options
   * @returns {LRUCache} Configured LRU cache for autocomplete
   */
  static createAutocompleteCache(options = {}) {
    const autocompleteOptions = {
      maxSize: 5 * BYTES_PER_MB,
      ttl: 1000 * 60 * 15, // 15 minutes
      updateAgeOnGet: true,
      sizeCalculation: (value) => {
        // Autocomplete suggestions are typically arrays of strings
        if (Array.isArray(value)) {
          return Math.max(MIN_SIZE, value.length * 150);
        }
        if (typeof value === 'string') {
          return LRUCacheUtils._calculateStringSize(value);
        }
        return 500; // Fallback
      },
      ...options,
    };

    return LRUCacheUtils.createCache(autocompleteOptions);
  }

  /**
   * Create a cache with size-based limits (for large data like images, PDFs)
   * @param {Object} options - Cache configuration options
   * @returns {LRUCache} Configured LRU cache with size limits
   */
  static createSizeBasedCache(options = {}) {
    const sizeOptions = {
      maxSize: 50 * BYTES_PER_MB,
      ttl: 1000 * 60 * 60 * 24, // 24 hours
      updateAgeOnGet: true,
      sizeCalculation: (value) => {
        // Fast path: Blob objects (most accurate size)
        if (value instanceof Blob) {
          return Math.max(MIN_SIZE, value.size || FALLBACK_SIZE);
        }
        
        // Handle PDF data using PDF utility functions (before string check)
        const pdfSize = calculatePdfSize(value);
        if (pdfSize > 0) {
          return pdfSize;
        }
        
        // Handle strings (common case)
        if (typeof value === 'string') {
          return LRUCacheUtils._calculateStringSize(value);
        }
        
        // Handle objects (including objects with PDF data that wasn't detected)
        if (value && typeof value === 'object') {
          return LRUCacheUtils._calculateObjectSize(value, FALLBACK_SIZE);
        }
        
        return FALLBACK_SIZE; // Fallback for primitives
      },
      dispose: (value) => {
        // Clean up blob URLs to prevent memory leaks
        if (value && typeof value === 'string' && value.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(value);
          } catch (e) {
            // Silently fail if URL is already revoked or invalid
          }
        }
      },
      ...options,
    };

    return LRUCacheUtils.createCache(sizeOptions);
  }
}

export default LRUCacheUtils;
